import { SPINNER_DOLPHIN } from '../../sim/dolphins';
import { clockLabel } from '../../ui/observation';
import { HABITAT_NAMES, speciesVisitsNote, visitorsNote } from './visitors';
import type { World } from '../../sim/types';
import { reefOf } from '../../sim/data/sites';
import { displayName, speciesOf } from '../../sim/species';
import { FISH_KINDS } from '../fishKinds';
import type { Place } from '../../ui/visit';
import type { Exhibit, Habitat } from '../exhibit';
import type { ReefModel, ReefTextures } from '../reefOutcrops';
import { YOUNG_SEEN_MM, focusIsland, mauiNuiReef, mauiNuiReefData, waterSceneData, type MauiNuiReefData, type ReefLocation } from './data';
import { DEPTH_BINS, ISLAND_NAMES, binSpan, type ReefIsland } from '../../sim/reef';
import { REEF_SCENE, ReefScene } from './reefScene';
import { OCTOPUS_DRAW } from './octopuses';
import { PelagicScene } from './pelagicScene';
import { BenthicScene } from './benthicScene';
import type { HabitatScene } from './habitatScene';

/**
 * Which scene each habitat opens (M110, the user's third brief, section 7: "Create configurations and scene types for
 * Upper Slope, Twilight, Deep Ocean"): the Reef its own (`ReefScene`); the open water's, the blue water's and the
 * twilight's (`PelagicScene`); the floor's below the reef, the slope's ledges and the deep's sediment
 * (`BenthicScene`); a site's own habitats taken the same way (the Cape Peninsula's rocky reef as ledges, its shelf as
 * sediment, M98). Each habitat's depths are the whole tank's (`exhibit.ts`'s boxes), so a site's are its own.
 */
export const HABITAT_SCENES: Record<Habitat['id'], { scene: 'reef' | 'pelagic' | 'benthic'; style?: 'ledges' | 'sediment' | 'blue' | 'twilight' }> = {
  reef: { scene: 'reef' },
  blue: { scene: 'pelagic', style: 'blue' },
  twilight: { scene: 'pelagic', style: 'twilight' },
  slope: { scene: 'benthic', style: 'ledges' },
  deep: { scene: 'benthic', style: 'sediment' },
  rocky: { scene: 'benthic', style: 'ledges' },
  shelf: { scene: 'benthic', style: 'sediment' },
};

/** A habitat scene opened: the scene, the key its camera's view is remembered by, how the day is laid on it, and what the panel says it is. */
export interface OpenedHabitat {
  readonly habitat: Habitat['id'];
  readonly scene: HabitatScene;
  readonly key: string;
  /** Lays the world's day on the scene, by the adapter (`data.ts`): the simulation's state read, never written. */
  update(world: World, place: Place): void;
  /** What the scene draws and how, for the sanctuary card's Drawn row. */
  note(): string;
  /** The reef shown as one island's own, or the whole of Maui Nui's (M287, `focusIsland`); the reef's alone. */
  setFocus?(island: ReefIsland | null, world: World, place: Place): void;
  /**
   * The reef laid from another world's day in place of today's (M287's before-and-after: the player's first day, rebuilt
   * from the seed), or today's again with null; the reef's alone.
   */
  setThen?(then: { world: World; place: Place } | null, world: World, place: Place): void;
}

/**
 * The least real time between two lays of a new day on an opened habitat, ms (review 26: the Reef's lay, 70 to 100 ms
 * MEASURED, came every game day, at 128 times speed's 39 ms day every two or three frames). Set against the speeds:
 * at 1 and 4 times a day lasts 5 and 1.25 s, each day still laid; above them the coral, which moves by a day's
 * fraction of a percent, is laid once a second.
 */
export const RELAY_MS = 1000;

/**
 * Whether the opened habitat is given the world's day again (`OpenedHabitat.update`): a new world (a load, a new game)
 * at once, a new day once `RELAY_MS` has passed since the last lay. A day passed over waits for the next frame due, so
 * the last day reached is always laid.
 */
export function relayDue(last: { world: World; day: number; at: number } | null, world: World, now: number): boolean {
  if (last?.world !== world) return true;
  return last.day !== world.day && now - last.at >= RELAY_MS;
}

/** A habitat's depths, m, from the whole tank's box (its shares of the tank's depth), the surface's at least 0. */
export function depthsOf(habitat: Habitat, exhibit: Exhibit): readonly [number, number] {
  return [Math.max(0, exhibit.depthAt(Math.max(0, habitat.down[0]))), Math.max(1, exhibit.depthAt(habitat.down[1]))];
}

/**
 * Opens a habitat's scene on the world's day, or null where it cannot be (a reef where the site has none). The reef is
 * one habitat, Maui Nui Reef (M118): the whole reef, every stratum of it drawn in one scene, none a place of its own.
 */
export function openHabitat(world: World, place: Place, habitat: Habitat, exhibit: Exhibit, model: { model: ReefModel; textures?: ReefTextures } | null): OpenedHabitat | null {
  const how = HABITAT_SCENES[habitat.id];
  if (how.scene === 'reef') {
    const reef = reefOf(place);
    if (!reef || !place.coral) return null;
    const scene = new ReefScene(mauiNuiReef(reef));
    if (model) scene.setReefModel(model.model, model.textures);
    let data: MauiNuiReefData | null = null;
    let laidFor: World | null = null;
    let focus: ReefIsland | null = null;
    let then: { world: World; place: Place } | null = null;
    const opened: OpenedHabitat = {
      habitat: habitat.id, scene, key: `${place.siteId}:reef`,
      update(today, todayPlace) {
        // The first day's reef in place of today's while it is asked for (its world fixed, its day its own).
        const [w, p] = then ? [then.world, then.place] : [today, todayPlace];
        // Another world (a game loaded, a new one) its counts afresh, as on an opening.
        if (laidFor !== null && w !== laidFor) scene.newWorld();
        laidFor = w;
        const whole = mauiNuiReefData(w, p, scene.middleDepthM);
        data = whole && focus ? focusIsland(whole, focus) : whole;
        if (data) scene.setData(data);
      },
      note: () => `${then ? `As it was on ${data?.simulationDate ?? 'your first day'}, your first day here, rebuilt from the game's seed (the same world replayed to its start). ` : ''}${reefNote(scene, data)}`,
      setFocus(island, w, p) {
        if (island === focus) return;
        focus = island;
        // Another island's fish and coral counted afresh, as another world's are.
        scene.newWorld();
        opened.update(w, p);
      },
      setThen(next, w, p) {
        if (next?.world === then?.world) return;
        then = next;
        opened.update(w, p);
      },
    };
    opened.update(world, place);
    return opened;
  }
  const depthRangeM = depthsOf(habitat, exhibit);
  const name = `${habitat.name.toLowerCase()}, ${Math.round(depthRangeM[0])} to ${Math.round(depthRangeM[1])} m`;
  const scene = how.scene === 'pelagic'
    ? new PelagicScene(habitat.id, name, depthRangeM, how.style === 'twilight' ? 'twilight' : 'blue')
    : new BenthicScene(habitat.id, name, depthRangeM, how.style === 'sediment' ? 'sediment' : 'ledges');
  let laidFor: World | null = null;
  const opened: OpenedHabitat = {
    habitat: habitat.id, scene, key: `${place.siteId}:${habitat.id}`,
    update(w, p) {
      // Another world (a game loaded, a new one) its visitors' looks afresh, as on an opening.
      if (laidFor !== null && w !== laidFor) scene.newWorld();
      laidFor = w;
      scene.setData(waterSceneData(w, p, habitat.id, name, depthRangeM));
    },
    note: () => scene.note(),
  };
  opened.update(world, place);
  return opened;
}

/** The Reef's Drawn row's spinner dolphins (M216): today's group over the hectare, or how often one comes; none where the site has none. */
export function dolphinsNote(data: Pick<MauiNuiReefData, 'dolphins' | 'dolphinOdds'>): string | null {
  if (!(data.dolphinOdds > 0)) return null;
  return data.dolphins
    ? `spinner dolphins (naiʻa): ${data.dolphins.count} of a group of about ${Math.round(data.dolphins.groupMean)} resting over the hectare today, from ${clockLabel(data.dolphins.hours[0])} to ${clockLabel(data.dolphins.hours[1])} (Tyne et al. 2017's hours inshore), each drawn at its length`
    : `spinner dolphins (naiʻa): none over the hectare today; a group rests over a hectare of the reef on about one day in ${Math.round(1 / data.dolphinOdds)}, Maui Nui's recorders heard them near shore in ${Math.round(SPINNER_DOLPHIN.heardHourShare * 100)}% of hours (McElligott and Lammers 2021), a group covering a share of that reach (Olson et al. 2026)`;
}

/** A share as a whole percent, or to a tenth under 10. */
const pct = (x: number): string => (x < 10 ? x.toFixed(1) : x.toFixed(0));

/**
 * The reef's Drawn row: what it is, how each thing it draws comes from the twelve strata, and each stratum's own figures
 * (the brief's "detailed island/depth information can still be shown ... in an info panel").
 */
function reefNote(scene: ReefScene, data: MauiNuiReefData | null): string {
  // The simulated kinds' adults and young first (`FISH_KINDS`' two of each), then the counted (M231, review M231's: not
  // the simulation's, so told apart).
  const sprites = scene.fishSprites();
  const fish = sprites.slice(0, 2 * FISH_KINDS.length).reduce((a, b) => a + b, 0);
  const counted = data?.counted ?? [];
  const bands = DEPTH_BINS.map((_, k) => binSpan(k)).join(', ');
  const parts = [
    `Maui Nui Reef: the sanctuary's whole reef from the shore to 30 m, Maui's, Molokaʻi's, Lānaʻi's and Kahoʻolawe's, drawn as one reef ${REEF_SCENE.reef.lengthM} m long, its floor about 8 to 18 m deep, shallower toward the left and deeper toward the right (the reef's own 0 to 30 m eased toward its middle's, so it reads as one reef)`,
    'its coral, its bleaching, its sand and its fish drawn from all twelve of the places the simulation holds the reef for (an island\'s reef in a depth band), each by its share of the reef\'s floor, easing into one another along the reef and patch by patch with no line between them',
    `its coral the places' live cover and its bands' bleaching (Maui Nui's, the simulation holding no island's), as much of its floor as its cover, colony by colony and the rest painted on its pavement, pale or white as it bleaches and a skeleton where a heat episode under way has killed it`,
    `its ${fish} fish a sprite each, the adults and the young of ${YOUNG_SEEN_MM / 10} cm or more an average hectare of Maui Nui's reef holds (the simulation's fish are by island, not by depth; each group's count drawn held from day to day while its share stays within half a fish and a margin of it, so a share on a rounding's edge does not step a fish in and out), homed where the reef stands up, each island's schools where its character shows, each drawn ${REEF_SCENE.fish.drawnTimes} times its length to be seen`,
    ...counted.map((c) => `its ${c.byIsland.reduce((a, b) => a + b, 0)} ${displayName(speciesOf(c.species))} counted, not simulated: NOAA's divers' count on each island's hard bottom spread over its reef's floor, its adults and its young of ${YOUNG_SEEN_MM / 10} cm or more, a sprite each, the count held`),
    'its sand, turf and algae as NOAA\'s surveys found each place\'s bottom, 2015 to 2024',
    'its water the sanctuary\'s clarity, chlorophyll and plankton',
    'its corals four of Hawaii\'s reef builders (lobe, finger, rice and cauliflower coral), their mix drawn, not simulated',
    'where the outcrops, rock and sand lie drawn, not simulated, the same every time',
  ];
  // One island's reef (M287): said first, so no one takes the drawing for the island's own layout.
  if (data?.focus) parts.unshift(`${ISLAND_NAMES[data.focus]}'s reef: every place on it drawn with ${ISLAND_NAMES[data.focus]}'s own live coral, turf and seaweed at its depth and its own fish a hectare, as the simulation holds them; the layout, the depths' places and the bleaching by depth the whole reef's, the visitors and the counted fish Maui Nui's`);
  if (data) {
    // Each island's cover in each band, each band's bleaching, each island's fish a hectare of its own reef.
    // An island's reef (M287) every place its strata: the island's own alone, the others' not drawn here.
    const covers = data.islands.filter((isl) => !data?.focus || isl.island === data.focus).map((isl) => {
      const mine = data?.reef.strata.map((l, j) => [l, data?.coverPercent[j] ?? 0] as const).filter(([l]) => l.island === isl.island) ?? [];
      return `${isl.name} ${mine.map(([, c]) => pct(c)).join(', ')}%`;
    });
    parts.push(`live coral by island (${bands}): ${covers.join('; ')}`);
    parts.push(`bleached by depth band (${bands}): ${data.bands.map((b) => `${pct(b.bleachedPercent)}%`).join(', ')}`);
    parts.push(visitorsNote(data.visitors, data.visitOdds));
    // Review M190: what the reef leaves undrawn named, as the open water's undrawn are; the hawksbill drawn among the
    // visitors since M196, the moano kea among the fish since M198, its yellow phase not.
    parts.push('the blue goatfish (moano kea) drawn in its usual phase alone: no count of how many are yellow was read');
    if (data.visits.length) parts.push(speciesVisitsNote(data.visits, HABITAT_NAMES));
    parts.push(`day octopuses (heʻe mauli): ${data.octopuses.length} in the hectare, ${data.octopusPerHa.toFixed(1)} a hectare of Maui Nui's reef, one to a den, the simulation's by island; each in its den by night and at midday, out foraging twice a day, once in the first ${OCTOPUS_DRAW.activeH} hours of daylight and once in the last, ${OCTOPUS_DRAW.forageMin} minutes each (Forsythe and Hanlon 1997; Ivey 2007, Kāneʻohe Bay), drawn changing its pattern ${OCTOPUS_DRAW.changesPerMin} times a minute on average (Hanlon, Forsythe and Joneschild 1999), each change fading over ${OCTOPUS_DRAW.fadeS} seconds`);
    // The spinner dolphins resting over it on their days (M216).
    const dolphins = dolphinsNote(data);
    if (dolphins) parts.push(dolphins);
    parts.push(`reef fish adults a hectare of each island's reef: ${data.islands.map((isl) => `${isl.name} ${pct(isl.fish.reduce((n, f) => n + f.perHa.value, 0))} (its reef ${pct(100 * isl.areaShare)}% of Maui Nui's)`).join('; ')}`);
    for (const c of counted) parts.push(`${displayName(speciesOf(c.species))}, counted, every size, a hectare of each island's reef: ${data.islands.map((isl, i) => `${isl.name} ${pct(c.perHaByIsland[i] ?? 0)}`).join('; ')}`);
  }
  return parts.join('; ');
}

export type { ReefLocation };
